import { useSchemaMutation } from '@frontend/react-query-helpers';
import { SchemaIO } from '@frontend/schema';
import { SchemaSignatureV1Service, serviceName } from '../service';
import { MutationEndpointName, ServiceMutations } from '../types';

export type UseSignatureV1MutationArgs<
  EndpointName extends MutationEndpointName,
  E = unknown,
  C = unknown,
  OtherOptions extends object = never,
  RequestOverride extends SchemaIO<ServiceMutations[EndpointName]>['input'] = SchemaIO<
    ServiceMutations[EndpointName]
  >['input']
> = Omit<
  Parameters<typeof useSchemaMutation<ServiceMutations, EndpointName, E, C, OtherOptions, RequestOverride>>[0],
  'service' | 'serviceName'
>;

/**
 * A wrapper around useSchemaMutation that uses the SMSSignatureV1 service and serviceName.
 * This is intended to only be used internally in the `mutations` directory of the library and not exported.
 */
export const useSignatureV1Mutation = <
  EndpointName extends MutationEndpointName,
  E = unknown,
  C = unknown,
  OtherOptions extends object = never,
  RequestOverride extends SchemaIO<ServiceMutations[EndpointName]>['input'] = SchemaIO<
    ServiceMutations[EndpointName]
  >['input']
>(
  args: UseSignatureV1MutationArgs<EndpointName, E, C, OtherOptions, RequestOverride>
) =>
  useSchemaMutation<ServiceMutations, EndpointName, E, C, OtherOptions, RequestOverride>({
    service: SchemaSignatureV1Service,
    serviceName,
    ...args,
  });

export type UseSignatureV1MutationEndpointArgs<
  EndpointName extends MutationEndpointName,
  E = unknown,
  C = unknown,
  OtherOptions extends object = never,
  RequestOverride extends SchemaIO<ServiceMutations[EndpointName]>['input'] = SchemaIO<
    ServiceMutations[EndpointName]
  >['input']
> = Omit<UseSignatureV1MutationArgs<EndpointName, E, C, OtherOptions, RequestOverride>, 'endpointName'> & {
  optimisticUpdate?: boolean;
};
